import { isEqual as dateEqual, format as formatDate, isValid, parse } from "date-fns";
import { computed, type Ref, ref, unref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { PRICE_OVERRIDE_API_DATE_FORMAT } from "@/constants.ts";

export const useUpdateDecisionDates = () => {
  const route = useRoute();
  const router = useRouter();

  const decisionDateStart = ref<Date | undefined>(undefined);
  const decisionDateEnd = ref<Date | undefined>(undefined);

  watch(
    () => route.query,
    async (newParams, oldParams) => {
      if (newParams !== oldParams) {
        const newDecisionDateStart = parse(
          `${newParams.decisionDateStart}`,
          PRICE_OVERRIDE_API_DATE_FORMAT,
          new Date(),
        );
        if (isValid(newDecisionDateStart) && !dateEqual(newDecisionDateStart, decisionDateStart.value || 0)) {
          decisionDateStart.value = newDecisionDateStart;
        } else if (!isValid(newDecisionDateStart)) {
          decisionDateStart.value = undefined;
        }

        const newDecisionDateEnd = parse(`${newParams.decisionDateEnd}`, PRICE_OVERRIDE_API_DATE_FORMAT, new Date());
        if (isValid(newDecisionDateEnd) && !dateEqual(newDecisionDateEnd, decisionDateEnd.value || 0)) {
          decisionDateEnd.value = newDecisionDateEnd;
        } else if (!isValid(newDecisionDateEnd)) {
          decisionDateEnd.value = undefined;
        }
      }
    },
    { immediate: true },
  );

  const updateDecisionDateStart = async (updatedDecisionDateStart?: Date) => {
    decisionDateStart.value = updatedDecisionDateStart;
    await router.push({
      query: {
        ...route.query,
        decisionDateStart: updatedDecisionDateStart
          ? `${formatDate(updatedDecisionDateStart, PRICE_OVERRIDE_API_DATE_FORMAT)}`
          : undefined,
      },
    });
  };

  const updateDecisionDateEnd = async (updatedDecisionDateEnd?: Date) => {
    decisionDateEnd.value = updatedDecisionDateEnd;
    await router.push({
      query: {
        ...route.query,
        decisionDateEnd: updatedDecisionDateEnd
          ? `${formatDate(updatedDecisionDateEnd, PRICE_OVERRIDE_API_DATE_FORMAT)}`
          : undefined,
      },
    });
  };

  const updateDates = async (updatedDecisionDateStart?: Date, updatedDecisionDateEnd?: Date) => {
    decisionDateStart.value = updatedDecisionDateStart;
    decisionDateEnd.value = updatedDecisionDateEnd;
    await router.push({
      query: {
        ...route.query,
        decisionDateStart: updatedDecisionDateStart
          ? `${formatDate(updatedDecisionDateStart, PRICE_OVERRIDE_API_DATE_FORMAT)}`
          : undefined,
        decisionDateEnd: updatedDecisionDateEnd
          ? `${formatDate(updatedDecisionDateEnd, PRICE_OVERRIDE_API_DATE_FORMAT)}`
          : undefined,
      },
    });
  };

  const updateDecisionDates = async (value: Date | (Date | null)[] | Date[] | null | undefined) => {
    if (Array.isArray(value) && value.length === 2) {
      const [startDate, endDate] = value;
      await updateDates(startDate || undefined, endDate || undefined);
    } else if (value instanceof Date) {
      await updateDecisionDateStart(value);
    } else {
      await updateDates(undefined, undefined);
    }
  };

  const dates = computed<Array<Date | null>>(() => [
    unref(decisionDateStart) || new Date(),
    unref(decisionDateEnd) || null,
  ]);

  return { dates, decisionDateEnd, decisionDateStart, updateDecisionDates };
};
